import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Crown, Check, Loader2, CreditCard } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useApp } from "@/store";

const SYM = { USD: "$", BRL: "R$", AUD: "A$" };
const PERK_KEYS = ["plan_perk1", "plan_perk2", "plan_perk3", "plan_perk4"];

export const PlanCard = () => {
  const { t } = useApp();
  const [plan, setPlan] = useState(null);
  const [currency, setCurrency] = useState("USD");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/plan").then(({ data }) => setPlan(data)).catch(() => setPlan({ is_pro: false, prices: {} }));
  useEffect(() => { load(); }, []);

  const subscribe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/plan/checkout", { currency, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setBusy(false); }
  };
  const portal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/plan/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === "string" && !d.startsWith("<") ? d : t("plan_portal_error")); setBusy(false); }
  };
  const cancel = async () => {
    if (!window.confirm(t("plan_confirm_cancel"))) return;
    setBusy(true);
    try { await api.post("/plan/cancel"); toast.success(t("plan_cancel_ok")); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!plan) return null;
  const p = plan.plan || {};
  const active = plan.is_pro;

  return (
    <div id="plan-card" data-testid="plan-card" className={`mt-10 rounded-2xl p-6 ${active ? "bg-[#002FA7] text-white" : "border border-[#002FA7]/30 bg-[#002FA7]/[0.03]"}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 font-display text-lg font-bold"><Crown size={18} className={active ? "text-yellow-300" : "text-[#FF3B30]"} /> {t("plan_name")} {active && <span data-testid="plan-active-badge" className="ml-1 rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">{t("plan_active")}</span>}</div>
          <ul className="mt-3 space-y-1.5 text-sm">
            {PERK_KEYS.map((k) => <li key={k} className="flex items-center gap-2"><Check size={14} className={active ? "text-yellow-300" : "text-[#155724]"} /> {t(k)}</li>)}
          </ul>
          {active && p.current_period_end && <p className="mt-3 text-xs opacity-80" data-testid="plan-period">{p.cancel_at_period_end ? t("plan_ends") : t("plan_renews")} {new Date(p.current_period_end).toLocaleDateString()}</p>}
          {!active && <p className="mt-3 text-xs text-gray-500">{t("plan_free_hint").replace("{n}", plan.free_import_limit)}</p>}
        </div>
        <div className="flex flex-col items-end gap-2">
          {active ? (
            <>
              <button data-testid="plan-portal" disabled={busy} onClick={portal} className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2 text-sm font-bold text-[#002FA7] hover:-translate-y-0.5 transition-transform disabled:opacity-60"><CreditCard size={15} /> {t("plan_manage_billing")}</button>
              {!p.cancel_at_period_end && <button data-testid="plan-cancel" disabled={busy} onClick={cancel} className="rounded-full border border-white/50 px-5 py-2 text-sm font-bold hover:bg-white/10 disabled:opacity-60">{t("plan_cancel")}</button>}
            </>
          ) : (
            <>
              <div className="flex items-center gap-1 rounded-full border border-gray-200 bg-white p-0.5" data-testid="plan-currency">
                {Object.keys(plan.prices || {}).map((c) => (
                  <button key={c} data-testid={`plan-cur-${c}`} onClick={() => setCurrency(c)} className={`rounded-full px-3 py-1 text-xs font-bold ${currency === c ? "bg-[#002FA7] text-white" : "text-gray-500"}`}>{c}</button>
                ))}
              </div>
              <div className="font-display text-2xl font-extrabold text-[#002FA7]" data-testid="plan-price">{SYM[currency]}{plan.prices?.[currency]}<span className="text-sm font-normal text-gray-400">{t("per_month")}</span></div>
              <button data-testid="plan-subscribe" disabled={busy} onClick={subscribe} className="inline-flex items-center gap-2 rounded-full bg-[#FF3B30] px-6 py-2.5 text-sm font-bold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Crown size={15} />} {t("plan_upgrade")}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
